import {
  Award,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  GraduationCap,
  Heart,
  Home,
  Info,
  CreditCard,
  IdCard,
  Layers,
  Mail,
  Phone,
  Sparkles,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GENDER_LABEL } from '@/constants/institute';
import { ROLE_LABEL } from '@/constants/roles';
import { MARITAL_STATUS_LABEL } from '@/features/institute/staff/constants';
import { formatDate } from '@/lib/format';
import type { OwnStaffProfile } from '@/types/staff';
import { ProfileField } from './ProfileField';
import type { TaughtSection } from './profileSummary';

export interface TeacherPersonalInfoTabProps {
  profile: OwnStaffProfile;
  sections: TaughtSection[];
  subjects: string[];
}

/**
 * The last four characters only, behind dots — "•••• 0123".
 *
 * The page shows identity and bank numbers this way even to their owner: it is
 * often on a staff-room screen, and the full value is one click away in the
 * edit form, which only they can open.
 */
function lastFour(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.length <= 4 ? value : `•••• ${value.slice(-4)}`;
}

/** "+91 98765 43210" from the ten digits the record holds. */
export function formatMobile(mobile: string | null | undefined): string | null {
  if (!mobile) return null;
  return mobile.length === 10 ? `+91 ${mobile.slice(0, 5)} ${mobile.slice(5)}` : mobile;
}

/**
 * The first tab of My Profile: who they are, how to reach them, and a summary
 * of their work.
 *
 * Edited from the page's Edit Profile button. What is shown here and on the
 * Professional tab as the school's — role, employee ID, joining date, employment
 * terms — is the school office's to change, and the page says so.
 */
export function TeacherPersonalInfoTab({
  profile,
  sections,
  subjects,
}: TeacherPersonalInfoTabProps) {
  const navigate = useNavigate();
  const details = profile.details;
  const experience =
    details?.experienceYears != null
      ? `${details.experienceYears} ${details.experienceYears === 1 ? 'year' : 'years'}`
      : null;

  return (
    <div className="space-y-7">
      <p className="flex items-center gap-2 text-sm text-ink-2">
        <Info className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
        Use Edit Profile to update your details. Your role, employee ID, joining date and employment
        terms are kept by the school office.
      </p>

      {/* Top Row: Personal Info & Contact Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card px-4 py-3 space-y-6">
          <div className="flex items-center gap-3 border-b border-line pb-4">
            <User className="w-6 h-6 text-indigo-600 dark:text-indigo-400" aria-hidden />
            <h3 className="text-lg font-black text-ink">Personal Information</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <ProfileField
              icon={<User className="w-5 h-5" />}
              tone="bg-indigo-500/10 border-indigo-500/20 text-indigo-600 dark:text-indigo-400"
              label="Full Name"
              value={profile.fullName}
            />
            <ProfileField
              icon={<span className="text-xs font-black font-mono">ID</span>}
              tone="bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
              label="Employee ID"
              value={profile.employeeCode}
              mono
            />
            <ProfileField
              icon={<Calendar className="w-5 h-5" />}
              tone="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
              label="Date of Birth"
              value={details?.dateOfBirth ? formatDate(details.dateOfBirth) : null}
            />
            <ProfileField
              icon={<Calendar className="w-5 h-5" />}
              tone="bg-cyan-500/10 border-cyan-500/20 text-cyan-600 dark:text-cyan-400"
              label="Joining Date"
              value={profile.joinedOn ? formatDate(profile.joinedOn) : null}
            />
            <ProfileField
              icon={<Sparkles className="w-5 h-5" />}
              tone="bg-pink-500/10 border-pink-500/20 text-pink-600 dark:text-pink-400"
              label="Gender"
              value={details?.gender ? GENDER_LABEL[details.gender] : null}
            />
            <ProfileField
              icon={<span className="text-sm font-black">🩸</span>}
              tone="bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400"
              label="Blood Group"
              value={details?.bloodGroup}
            />
            <ProfileField
              icon={<Heart className="w-5 h-5" />}
              tone="bg-red-500/10 border-red-500/20 text-red-600 dark:text-red-400"
              label="Marital Status"
              value={details?.maritalStatus ? MARITAL_STATUS_LABEL[details.maritalStatus] : null}
            />
            <ProfileField
              icon={<Mail className="w-5 h-5" />}
              tone="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
              label="Email"
              value={profile.email}
            />
          </div>
        </div>

        <div className="card px-4 py-3 space-y-6">
          <div className="flex items-center gap-3 border-b border-line pb-4">
            <Phone className="w-6 h-6 text-indigo-600 dark:text-indigo-400" aria-hidden />
            <h3 className="text-lg font-black text-ink">Contact Information</h3>
          </div>

          <div className="space-y-5">
            <ProfileField
              icon={<Phone className="w-5 h-5" />}
              tone="bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
              label="Mobile Number"
              value={formatMobile(profile.mobile)}
            />
            <ProfileField
              icon={<Phone className="w-5 h-5" />}
              tone="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
              label="Alternate Number"
              value={formatMobile(details?.alternateMobile)}
            />
            <ProfileField
              icon={<Home className="w-5 h-5" />}
              tone="bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400"
              label="Present Address"
              value={
                details?.presentAddress ? (
                  <span className="whitespace-pre-line leading-relaxed">
                    {details.presentAddress}
                  </span>
                ) : null
              }
            />
            <ProfileField
              icon={<Home className="w-5 h-5" />}
              tone="bg-violet-500/10 border-violet-500/20 text-violet-600 dark:text-violet-400"
              label="Permanent Address"
              value={
                details?.permanentAddress ? (
                  <span className="whitespace-pre-line leading-relaxed">
                    {details.permanentAddress}
                  </span>
                ) : null
              }
            />
            <ProfileField
              icon={<Heart className="w-5 h-5" />}
              tone="bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400"
              label="Emergency Contact"
              value={
                details?.emergencyContactName || details?.emergencyContactMobile
                  ? [details.emergencyContactName, formatMobile(details.emergencyContactMobile)]
                      .filter(Boolean)
                      .join(' · ')
                  : null
              }
            />
          </div>
        </div>
      </div>

      {/* Identity & bank — masked on screen, whole in the edit form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card px-4 py-3 space-y-4">
          <div className="flex items-center gap-2.5 border-b border-line pb-3.5">
            <IdCard className="w-5 h-5 text-purple-600 dark:text-purple-400" aria-hidden />
            <h4 className="text-base font-black text-ink">Identity</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <ProfileField
              icon={<IdCard className="w-4 h-4" />}
              tone="bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400"
              label="Aadhaar"
              value={lastFour(details?.aadhaarNumber)}
              mono
            />
            <ProfileField
              icon={<IdCard className="w-4 h-4" />}
              tone="bg-indigo-500/10 border-indigo-500/20 text-indigo-600 dark:text-indigo-400"
              label="PAN"
              value={lastFour(details?.panNumber)}
              mono
            />
            <ProfileField
              icon={<IdCard className="w-4 h-4" />}
              tone="bg-sky-500/10 border-sky-500/20 text-sky-600 dark:text-sky-400"
              label="UAN"
              value={lastFour(details?.uanNumber)}
              mono
            />
            <ProfileField
              icon={<IdCard className="w-4 h-4" />}
              tone="bg-teal-500/10 border-teal-500/20 text-teal-600 dark:text-teal-400"
              label="Passport"
              value={lastFour(details?.passportNumber)}
              mono
            />
          </div>
        </div>

        <div className="card px-4 py-3 space-y-4">
          <div className="flex items-center gap-2.5 border-b border-line pb-3.5">
            <CreditCard className="w-5 h-5 text-violet-600 dark:text-violet-400" aria-hidden />
            <h4 className="text-base font-black text-ink">Bank Account</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <ProfileField
              icon={<Building2 className="w-4 h-4" />}
              tone="bg-violet-500/10 border-violet-500/20 text-violet-600 dark:text-violet-400"
              label="Bank"
              value={details?.bankName}
            />
            <ProfileField
              icon={<User className="w-4 h-4" />}
              tone="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
              label="Account Holder"
              value={details?.bankAccountHolder}
            />
            <ProfileField
              icon={<CreditCard className="w-4 h-4" />}
              tone="bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
              label="Account Number"
              value={lastFour(details?.bankAccountNumber)}
              mono
            />
            <ProfileField
              icon={<Building2 className="w-4 h-4" />}
              tone="bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400"
              label="IFSC"
              value={details?.bankIfsc}
              mono
            />
          </div>
        </div>
      </div>

      {/* Bottom Row: work at a glance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card px-4 py-3 space-y-4">
          <div className="flex items-center gap-2.5 border-b border-line pb-3.5">
            <Briefcase className="w-5 h-5 text-amber-600 dark:text-amber-400" aria-hidden />
            <h4 className="text-base font-black text-ink">Professional Details</h4>
          </div>

          <div className="space-y-4">
            <ProfileField
              icon={<User className="w-4 h-4" />}
              tone="bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400"
              label="Designation"
              value={profile.designation}
            />
            <ProfileField
              icon={<Layers className="w-4 h-4" />}
              tone="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
              label="Role"
              value={profile.roleName ?? ROLE_LABEL[profile.staffType]}
            />
            <ProfileField
              icon={<Building2 className="w-4 h-4" />}
              tone="bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400"
              label="School"
              value={profile.instituteName}
            />
            <ProfileField
              icon={<Award className="w-4 h-4" />}
              tone="bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400"
              label="Experience"
              value={experience}
            />
          </div>
        </div>

        <div className="card px-4 py-3 space-y-4">
          <div className="flex items-center gap-2.5 border-b border-line pb-3.5">
            <BookOpen className="w-5 h-5 text-amber-600 dark:text-amber-400" aria-hidden />
            <h4 className="text-base font-black text-ink">Subjects & Classes</h4>
          </div>

          <div className="space-y-4">
            <ProfileField
              icon={<BookOpen className="w-4 h-4" />}
              tone="bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400"
              label="Subjects Handled"
              value={
                subjects.length > 0 ? (
                  <span className="flex flex-wrap gap-1.5">
                    {subjects.map((subject) => (
                      <span
                        key={subject}
                        className="bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800/40 text-xs font-extrabold px-3 py-1 rounded-md"
                      >
                        {subject}
                      </span>
                    ))}
                  </span>
                ) : null
              }
            />
            <ProfileField
              icon={<GraduationCap className="w-4 h-4" />}
              tone="bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400"
              label="Classes Handled"
              value={
                sections.length > 0 ? (
                  <span className="flex flex-wrap gap-1.5">
                    {sections.map((section) => (
                      <span
                        key={section.uuid}
                        className="bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30 text-xs font-black px-3 py-1 rounded-full"
                      >
                        {section.displayName}
                      </span>
                    ))}
                  </span>
                ) : null
              }
            />
            <button
              type="button"
              onClick={() => navigate('/teacher/timetable')}
              className="text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1 cursor-pointer"
            >
              <span>View My Timetable</span>
              <span aria-hidden>&rarr;</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
